import { ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import {
  Campo,
  Casilla,
  Entrada,
  EntradaConPrefijo,
  Selector,
} from '@/components/ui/Campo'
import { useAvisar } from '@/components/ui/avisador-contexto'
import {
  clienteEntrada,
  idDeCarpetaDrive,
  type ClienteAdmin,
  type ClienteEntrada,
} from '@/lib/schemas/admin'
import { BotonDriveEnlace } from '@/features/publicacion/ArchivosDeDrive'
import { useGuardarCliente, useProfesionales } from './api'
import { useFormulario } from './formulario'
import { ModalMarco } from './ModalMarco'

interface ModalClienteProps {
  abierto: boolean
  alCerrar: () => void
  /** Sin cliente: alta. */
  cliente: ClienteAdmin | null
  alGuardar?: (c: ClienteAdmin) => void
}

/**
 * C8 · Nuevo / editar cliente. La carpeta de Drive se elige con el selector
 * de Google (Fase 6) o se pega su enlace o ID.
 * Cada apertura monta el formulario de cero (la `key`).
 */
export function ModalCliente(props: ModalClienteProps) {
  if (!props.abierto) return null
  return <Formulario key={props.cliente?.id ?? 'nuevo'} {...props} />
}

function Formulario({ abierto, alCerrar, cliente, alGuardar }: ModalClienteProps) {
  const guardar = useGuardarCliente()
  const profesionales = useProfesionales()
  const avisar = useAvisar()
  const [valores, setValores] = useState<ClienteEntrada>(() =>
    cliente ? desde(cliente) : vacio(),
  )
  const form = useFormulario(
    clienteEntrada,
    async (_datos, crudos) => {
      const c = await guardar.mutateAsync({ id: cliente?.id ?? null, datos: crudos })
      avisar(cliente ? 'Cliente guardado' : `Cliente ${c.nombre} creado`)
      alGuardar?.(c)
    },
    alCerrar,
  )

  const cambiar = <K extends keyof ClienteEntrada>(k: K, v: ClienteEntrada[K]) => {
    setValores((a) => ({ ...a, [k]: v }))
  }

  const idCarpeta = idDeCarpetaDrive(valores.driveFolder)

  return (
    <ModalMarco
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={cliente ? 'Editar cliente' : 'Nuevo cliente'}
      guardar="Guardar"
      ocupado={form.ocupado}
      error={form.errorGeneral}
      alCambiar={form.limpiar}
      alEnviar={() => {
        form.validarYEnviar(valores)
      }}
    >
      <Campo etiqueta="Nombre" error={form.errores.nombre}>
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.nombre}
            onChange={(e) => {
              cambiar('nombre', e.target.value)
            }}
            autoFocus
            maxLength={120}
            autoComplete="organization"
          />
        )}
      </Campo>

      <Campo
        etiqueta="Usuario de Instagram"
        ayuda="Se usa en las vistas previas."
        error={form.errores.usuarioInstagram}
      >
        {(p) => (
          <EntradaConPrefijo
            prefijo="@"
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.usuarioInstagram}
            onChange={(e) => {
              cambiar('usuarioInstagram', e.target.value)
            }}
            maxLength={41}
            autoComplete="off"
            autoCapitalize="none"
          />
        )}
      </Campo>

      <BotonDriveEnlace
        carpetaId={null}
        modo="carpeta"
        multiple={false}
        titulo="Elegí la carpeta del cliente"
        etiqueta="Elegir carpeta de Drive"
        alElegir={(lista) => {
          const c = lista[0]
          if (c) cambiar('driveFolder', c.id)
        }}
      />

      <Campo
        etiqueta="Carpeta de Drive"
        ayuda={
          idCarpeta ? (
            <a
              className="inline-flex items-center gap-1 underline underline-offset-4"
              href={`https://drive.google.com/drive/folders/${idCarpeta}`}
              target="_blank"
              rel="noreferrer"
            >
              Abrir en Drive <ExternalLinkIcon className="size-3" aria-hidden />
            </a>
          ) : (
            'Elegila con el botón (abre en tu Mi unidad; también ves lo compartido con vos), o pegá el enlace de la carpeta del cliente.'
          )
        }
        error={form.errores.driveFolder}
      >
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.driveFolder}
            onChange={(e) => {
              cambiar('driveFolder', e.target.value)
            }}
            placeholder="https://drive.google.com/drive/folders/…"
            autoComplete="off"
            inputMode="url"
          />
        )}
      </Campo>

      <Campo
        etiqueta="Profesional asignada"
        ayuda="Los mails al cliente salen desde su casilla. Sin asignar, desde la de Serena."
      >
        {(p) => (
          <Selector
            id={p.id}
            aria-describedby={p.describedBy}
            value={valores.profesionalAsignadoId ?? ''}
            onChange={(e) => {
              cambiar('profesionalAsignadoId', e.target.value || null)
            }}
          >
            <option value="">Sin asignar</option>
            {(profesionales.data ?? []).map((pr) => (
              <option key={pr.id} value={pr.id}>
                {pr.nombre}
              </option>
            ))}
          </Selector>
        )}
      </Campo>

      <Casilla
        etiqueta="Activo"
        checked={valores.activo ?? true}
        onCheckedChange={(v) => {
          cambiar('activo', v === true)
        }}
      />
    </ModalMarco>
  )
}

const vacio = (): ClienteEntrada => ({
  nombre: '',
  usuarioInstagram: '',
  driveFolder: '',
  activo: true,
  profesionalAsignadoId: null,
})

const desde = (c: ClienteAdmin): ClienteEntrada => ({
  nombre: c.nombre,
  usuarioInstagram: c.usuarioInstagram,
  driveFolder: c.driveFolderId,
  activo: c.activo,
  profesionalAsignadoId: c.profesionalAsignadoId,
})
